/**
 * mergeWireEvents — accumulate the live game event tail across socket updates.
 *
 * The wire only carries a RECENT TAIL of `state.events` (WIRE_EVENT_LIMIT on
 * the server) plus the true `eventCount`. Crucially, an intermediate bot-loop
 * `GAME_STATE_PATCH` carries only the events generated SINCE the bot loop
 * started — often just one or two. If the client blindly REPLACES its cached
 * `state.events` with that short tail, the in-game "Recent activity" feed
 * collapses to those few rows after your turn (then grows again as more patches
 * stream in) — the "it clears, then starts loading again" report.
 *
 * The event log is append-only and every event has a stable ABSOLUTE position
 * (`eventCount - tail.length + i`). This merges the previous tail with the
 * incoming one by absolute position (incoming wins on overlap — it's the
 * authoritative board state) and returns the longest CONTIGUOUS tail ending at
 * the newest event, capped for memory. The feed therefore keeps accumulating
 * and streams new events in real time instead of clearing.
 */

export interface WireEventState {
  events?: unknown[]
  eventCount?: number
}

/** Upper bound on how many recent events the live cache retains. Comfortably
 *  larger than the wire tail and any feed's display cap, small enough to stay
 *  cheap. */
export const MAX_LIVE_EVENT_TAIL = 200

export function mergeWireEvents(
  prev: WireEventState | undefined,
  next: WireEventState,
): { events: unknown[]; eventCount: number } {
  const nextEvents = next.events ?? []
  const nextCount = next.eventCount ?? nextEvents.length
  const prevEvents = prev?.events ?? []
  const prevCount = prev?.eventCount ?? prevEvents.length

  // Index every known event by its ABSOLUTE position in the full log.
  const byIndex = new Map<number, unknown>()
  const prevOffset = prevCount - prevEvents.length
  for (let i = 0; i < prevEvents.length; i++) byIndex.set(prevOffset + i, prevEvents[i])
  // Incoming events win on overlap — they reflect the authoritative state.
  const nextOffset = nextCount - nextEvents.length
  for (let i = 0; i < nextEvents.length; i++) byIndex.set(nextOffset + i, nextEvents[i])

  const total = Math.max(prevCount, nextCount)

  // Longest CONTIGUOUS tail ending at the newest event (total-1). Contiguity
  // guards against a gap from a dropped patch producing a mis-addressed feed;
  // a gap simply bounds the tail to the newest unbroken run.
  const tail: unknown[] = []
  for (let i = total - 1; i >= 0 && tail.length < MAX_LIVE_EVENT_TAIL; i--) {
    const ev = byIndex.get(i)
    if (ev === undefined) break
    tail.unshift(ev)
  }

  return { events: tail, eventCount: total }
}
